import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { Alert, Button } from "@platform/design-system";

import { useAnalystTeams } from "../../../hooks/useAnalystTeams";
import {
  AnalystUser,
  AnalystTitlesResponse,
  UpdateAnalystRequest,
} from "../types";

interface AnalystFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  analyst: AnalystUser;
}

export const AnalystForm: React.FC<AnalystFormProps> = ({
  open,
  onClose,
  onSuccess,
  analyst,
}) => {
  const axiosInstance = useApi();

  // Fetch analyst titles for the dropdown
  const { data: titlesResponse, isLoading: titlesLoading } = useQuery<
    AnalystTitlesResponse,
    Error
  >({
    queryKey: ["analystTitles"],
    queryFn: async () => {
      const response = await axiosInstance.get<AnalystTitlesResponse>(
        "brokerportal/v1/analysts/titles",
      );
      return response.data;
    },
  });

  // Fetch analyst teams for the dropdown
  const { data: teamsResponse, isLoading: teamsLoading } = useAnalystTeams();

  const availableTitles = titlesResponse?.titles ?? [];
  const availableTeams = teamsResponse?.teams ?? [];

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    blocked: false,
    allowRfpAssignment: false,
    title: "",
    teamId: undefined as number | undefined,
    totalCapacityHours: undefined as number | undefined,
    startDate: "",
  });

  useEffect(() => {
    setFormData({
      email: analyst.email || "",
      firstName: analyst.givenName || "",
      lastName: analyst.familyName || "",
      blocked: analyst.blocked || false,
      allowRfpAssignment: analyst.allowRfpAssignment || false,
      title: analyst.title || "",
      teamId: analyst.teamId,
      totalCapacityHours: analyst.totalCapacityHours,
      startDate: analyst.startDate || "",
    });
  }, [analyst]);

  const updateMutation = useMutation<
    unknown,
    Error,
    UpdateAnalystRequest,
    unknown
  >({
    mutationFn: (data: UpdateAnalystRequest) =>
      axiosInstance.patch(`brokerportal/v1/analysts/${analyst.id}`, data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleInputChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleTeamChange = (
    event: SelectChangeEvent<string | number>,
  ): void => {
    const value =
      event.target.value === "" ? undefined : Number(event.target.value);
    setFormData((prev) => ({ ...prev, teamId: value }));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();

    const updateData: UpdateAnalystRequest = {
      email: formData.email,
      blocked: formData.blocked,
      allowRfpAssignment: formData.allowRfpAssignment,
      title: formData.title,
      teamId: formData.teamId,
      totalCapacityHours: formData.totalCapacityHours,
      startDate: formData.startDate,
    };

    updateMutation.mutate(updateData);
  };

  const handleClose = (): void => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      blocked: false,
      allowRfpAssignment: false,
      title: "",
      teamId: undefined,
      totalCapacityHours: undefined,
      startDate: "",
    });
    onClose();
  };

  const isLoading = updateMutation.isPending;

  const error = updateMutation.error as Error | null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Analyst</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error instanceof Error ? error.message : "An error occurred"}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={handleInputChange("email")}
              disabled // Don't allow email changes during edit
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={formData.firstName}
                onChange={handleInputChange("firstName")}
                disabled // Don't allow name changes during edit
              />

              <TextField
                label="Last Name"
                fullWidth
                required
                value={formData.lastName}
                onChange={handleInputChange("lastName")}
                disabled // Don't allow name changes during edit
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowRfpAssignment}
                  onChange={handleInputChange("allowRfpAssignment")}
                />
              }
              label="Allow RFPs to be assigned to this Analyst?"
            />
            <FormControl fullWidth>
              <InputLabel id="title-label" required>
                Title
              </InputLabel>
              <Select
                labelId="title-label"
                value={formData.title}
                label="Title"
                required
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                disabled={titlesLoading}
              >
                {availableTitles.map((titleOption) => (
                  <MenuItem key={titleOption.value} value={titleOption.value}>
                    {titleOption.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel id="team-select-label">Team</InputLabel>
              <Select
                labelId="team-select-label"
                id="team-select"
                name="team"
                value={formData.teamId?.toString() || ""}
                label="Team"
                onChange={handleTeamChange}
                disabled={teamsLoading}
              >
                <MenuItem value="">
                  <em>No Team</em>
                </MenuItem>
                {}
                {availableTeams.map((team: { id: number; name?: string }) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name ?? "Unnamed Team"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {}{" "}
            <TextField
              label="Total Capacity"
              type="number"
              fullWidth
              value={formData.totalCapacityHours ?? ""}
              onChange={(e): void => {
                const value =
                  e.target.value === "" ? undefined : Number(e.target.value);
                setFormData({ ...formData, totalCapacityHours: value });
              }}
              slotProps={{
                htmlInput: {
                  min: 0,
                  step: 0.5,
                },
              }}
              helperText="Total capacity in points for RFP assignments"
            />
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={formData.startDate}
              onChange={(e): void => {
                setFormData({ ...formData, startDate: e.target.value });
              }}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              helperText="Employment start date"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.blocked}
                  onChange={handleInputChange("blocked")}
                />
              }
              label="Account Disabled?"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="primary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Update Analyst
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
